import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Card } from '../../components/ui/Card';
import { api } from '../../lib/api';
import { Series, type Point } from './Series';

/**
 * Where people came from, from Cloudflare's beacon.
 *
 * ## Why this is not more of the overview page
 *
 * Everything else in admin counts accounts: plays, submissions, reports, money.
 * This counts strangers - people who arrived and may never sign in - and the two
 * cannot be added up. Keeping them on separate pages is what stops somebody
 * reading "564 page views" next to "35 listeners" and concluding one is a
 * subset of the other.
 *
 * ## Estimates, and the page says so
 *
 * Cloudflare samples, ad blockers eat the beacon, and the desktop app does not
 * carry it at all. Every number here is therefore a floor with error bars, and
 * dressing that up as precision would make it worse than useless - somebody
 * would eventually make a decision on the difference between two of them.
 *
 * ## No identity, by construction
 *
 * The beacon is privacy-first: it never sees who anybody is, so nothing on this
 * page can be joined to an account, and nothing on it needs to be handled as
 * personal data. That is a property worth keeping rather than working around.
 */
interface Row {
  label: string;
  pageviews: number;
  visits: number;
}

interface Traffic {
  available: boolean;
  reason: string | null;
  days: number;
  since: string | null;
  until: string | null;
  totals: { pageviews: number; visits: number };
  series: { at: string; pageviews: number; visits: number }[];
  hosts: Row[];
  paths: Row[];
  referrers: Row[];
  countries: Row[];
  devices: Row[];
  browsers: Row[];
  systems: Row[];
  vitals: {
    samples: number;
    lcp_ms: number | null;
    inp_ms: number | null;
    fcp_ms: number | null;
    ttfb_ms: number | null;
    cls: number | null;
  };
}

/** The windows the server will answer for. Anything else it rounds to a week. */
const WINDOWS = [
  { days: 1, label: '24 hours' },
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
];

export function AdminTrafficPage() {
  const [days, setDays] = useState(7);

  const { data, isPending, isError } = useQuery({
    queryKey: ['admin-traffic', days],
    queryFn: () => api.get<Traffic>(`/admin/analytics?days=${days}`),
    /*
     * The server caches this for ten minutes against Cloudflare's rate limit,
     * so refetching on every window focus would be a request per tab switch to
     * be handed the same cached answer back.
     */
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="flex flex-col gap-6" data-admin-traffic>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Traffic</h1>
          <p className="max-w-2xl text-[13px] leading-relaxed text-soft">
            From the Cloudflare beacon, which sees arrivals rather than accounts. It knows nothing
            about who anybody is, and it is the only thing here that can tell you where a visitor
            came from.
          </p>
        </div>

        <div
          role="radiogroup"
          aria-label="Time window"
          data-traffic-window
          className="flex shrink-0 items-center gap-1 rounded-[var(--radius-pill)] border border-line p-1"
        >
          {WINDOWS.map((window) => (
            <button
              key={window.days}
              type="button"
              role="radio"
              aria-checked={days === window.days}
              data-window={window.days}
              onClick={() => setDays(window.days)}
              className={`rounded-[var(--radius-pill)] px-3 py-1 font-mono text-[11px] transition-colors ${
                days === window.days
                  ? 'bg-accent text-accent-ink'
                  : 'text-soft hover:bg-raised hover:text-ink'
              }`}
            >
              {window.label}
            </button>
          ))}
        </div>
      </div>

      {isPending && <p className="font-mono text-[11px] text-faint">Asking Cloudflare…</p>}

      {/*
        The two ways there is nothing to draw, said differently, because the
        fixes are different: one is a missing credential and the other is a
        third party having an afternoon.
      */}
      {!isPending && (isError || data?.available === false) && (
        <Card className="flex flex-col gap-2 p-5" data-traffic-unavailable>
          <span className="meta-caps text-ghost">No numbers</span>
          <p className="text-[13px] leading-relaxed text-soft">
            {data?.reason === 'not_configured'
              ? 'This installation has no Cloudflare analytics token or site tag configured, so there is nothing to read. Both live in the environment beside the Stream credentials.'
              : 'Cloudflare did not answer. Nothing is wrong with the site; the numbers will be back when they are.'}
          </p>
        </Card>
      )}

      {data?.available && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Figure value={data.totals.visits} label="visits" tone="text-fresh" />
            <Figure value={data.totals.pageviews} label="page views" />
            <Figure
              value={data.totals.visits > 0
                ? Math.round((data.totals.pageviews / data.totals.visits) * 10) / 10
                : 0}
              label="pages per visit"
              tone="text-accent"
            />
            <Figure value={data.countries.length} label="countries" />
          </div>

          <Card className="flex flex-col gap-4 p-5">
            <div className="flex items-baseline justify-between gap-3">
              <span className="meta-caps text-ghost">
                {days === 1 ? 'By hour' : 'By day'}
              </span>
              <span className="font-mono text-[10px] text-ghost">
                page views, corrected for sampling
              </span>
            </div>

            <Series points={toPoints(data)} label="page view" />
          </Card>

          <div className="grid gap-4 lg:grid-cols-2">
            {/*
              Referrers first, because it is the question this page exists to
              answer and the only one nothing else on the site can. Our own
              hostnames are already filtered out server-side: this is a
              single-page application, so internal navigation reports the site
              itself as the referrer and would otherwise be every row.
            */}
            <Breakdown
              title="Where they came from"
              hint="our own pages excluded"
              rows={data.referrers}
              metric="visits"
              empty="Nothing but direct arrivals in this window."
            />

            <Breakdown
              title="What they landed on"
              rows={data.paths}
              metric="pageviews"
              empty="No pages recorded."
            />

            {/*
              Page views rather than visits here, unlike the referrers above.
              A visit is a session *started*, so a country somebody browsed from
              after arriving elsewhere reads as zero - and a list of countries
              with zeroes down it looks broken rather than precise.
            */}
            <Breakdown
              title="Countries"
              rows={data.countries}
              metric="pageviews"
              empty="No countries recorded."
            />

            <Breakdown
              title="Devices"
              rows={data.devices}
              metric="pageviews"
              empty="No devices recorded."
            />

            <Breakdown
              title="Browsers"
              rows={data.browsers}
              metric="pageviews"
              empty="No browsers recorded."
            />

            <Breakdown
              title="Operating systems"
              rows={data.systems}
              metric="pageviews"
              empty="Nothing recorded."
            />
          </div>

          {/*
            Both hostnames, when both are being used. It reads as a curiosity
            and it is not: a site answering on the apex and on www is splitting
            its own numbers, and every link out there points at one or the
            other.
          */}
          {data.hosts.length > 1 && (
            <Breakdown
              title="Hostnames"
              hint="the site answers on more than one"
              rows={data.hosts}
              metric="pageviews"
              empty=""
            />
          )}

          <Vitals vitals={data.vitals} />

          <p className="font-mono text-[10px] leading-relaxed text-ghost">
            Estimates. Cloudflare samples the data and this page corrects for it; ad blockers stop
            the beacon entirely, and the desktop app does not carry it. Treat these as a floor.
            Cached for ten minutes.
          </p>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * The series, as the chart wants it.
 *
 * `Series` is keyed and labelled by day, so an hourly window is handed the hour
 * as its key. The tooltip reads slightly oddly for a 24-hour window and the
 * shape is right, which is the trade worth making against a second chart
 * component that exists only to say "at" instead of "on".
 */
function toPoints(data: Traffic): Point[] {
  return data.series.map((point) => ({ date: point.at, value: point.pageviews }));
}

function Figure({ value, label, tone = 'text-ink' }: { value: number; label: string; tone?: string }) {
  return (
    <Card className="flex flex-col gap-1 p-4">
      <span className={`text-2xl font-bold tracking-tight ${tone}`}>{value.toLocaleString()}</span>
      <span className="font-mono text-[10px] text-faint">{label}</span>
    </Card>
  );
}

/**
 * One list, with a bar behind each row.
 *
 * The bar is proportional to the biggest row rather than to the total, because
 * the question these answer is "which of these is the big one" and a set of
 * slivers against a total answers it worse.
 */
function Breakdown({
  title,
  hint,
  rows,
  metric,
  empty,
}: {
  title: string;
  hint?: string;
  rows: Row[];
  metric: 'visits' | 'pageviews';
  empty: string;
}) {
  const peak = Math.max(1, ...rows.map((row) => row[metric]));

  return (
    <Card className="flex flex-col gap-3 p-5" data-breakdown={title}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="meta-caps text-ghost">{title}</span>
        {hint !== undefined && <span className="font-mono text-[10px] text-ghost">{hint}</span>}
      </div>

      {rows.length === 0 ? (
        <p className="font-mono text-[11px] text-faint">{empty}</p>
      ) : (
        <ol className="flex flex-col gap-1.5">
          {rows.map((row) => (
            <li key={row.label} className="relative flex items-center justify-between gap-3 py-1">
              <span
                aria-hidden
                className="absolute inset-y-0 left-0 rounded-[3px] bg-raised"
                style={{ width: `${Math.max(2, (row[metric] / peak) * 100)}%` }}
              />

              <span className="relative min-w-0 truncate px-2 font-mono text-[11px] text-soft">
                {row.label || '(none)'}
              </span>
              <span className="relative shrink-0 px-2 font-mono text-[11px] tabular-nums text-ink">
                {row[metric].toLocaleString()}
              </span>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

/**
 * Core Web Vitals, at the 75th percentile.
 *
 * The 75th because that is the number Google grades on and the one every other
 * tool quotes: an average hides the quarter of visits that were bad, and those
 * are the visits somebody left over.
 *
 * The thresholds are Google's published ones, and the colour is the whole
 * point - a number nobody can grade is a number nobody acts on.
 */
function Vitals({ vitals }: { vitals: Traffic['vitals'] }) {
  if (vitals.samples === 0) return null;

  return (
    <Card className="flex flex-col gap-3 p-5" data-web-vitals>
      <div className="flex items-baseline justify-between gap-3">
        <span className="meta-caps text-ghost">Web vitals · 75th percentile</span>
        <span className="font-mono text-[10px] text-ghost">
          {vitals.samples.toLocaleString()} measured
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Vital label="LCP" value={vitals.lcp_ms} unit="ms" good={2500} poor={4000} />
        <Vital label="INP" value={vitals.inp_ms} unit="ms" good={200} poor={500} />
        <Vital label="CLS" value={vitals.cls} unit="" good={0.1} poor={0.25} digits={3} />
        <Vital label="FCP" value={vitals.fcp_ms} unit="ms" good={1800} poor={3000} />
        <Vital label="TTFB" value={vitals.ttfb_ms} unit="ms" good={800} poor={1800} />
      </div>
    </Card>
  );
}

function Vital({
  label,
  value,
  unit,
  good,
  poor,
  digits = 0,
}: {
  label: string;
  value: number | null;
  unit: string;
  good: number;
  poor: number;
  digits?: number;
}) {
  const tone =
    value === null
      ? 'text-faint'
      : value <= good
        ? 'text-fresh'
        : value <= poor
          ? 'text-warning'
          : 'text-hot-text';

  return (
    <div className="flex flex-col gap-1">
      <span className={`text-lg font-bold tabular-nums ${tone}`}>
        {value === null ? '—' : `${value.toFixed(digits)}${unit}`}
      </span>
      <span className="font-mono text-[10px] text-faint">{label}</span>
    </div>
  );
}
